import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Select,
  Input,
  Button,
  Typography,
  Row,
  Col,
  Badge,
  Tag,
  message,
  Spin,
  Alert,
  Divider,
  Space
} from "antd";
import {
  CheckCircleFilled,
  ClockCircleFilled,
  CloseCircleFilled,
  FileTextOutlined,
  CloudUploadOutlined,
  FilePdfOutlined,
  PictureOutlined
} from "@ant-design/icons";
import axios from "axios";
import { useUserState } from "../Contexts/UserContext";
import KycUploadField from "../components/kyc/KycUploadField";
import w9FormAsset from "../assets/w9form/Blank-W-9.pdf";
import { getSafeUrl } from "../utils/helpers";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const BASE_URL = import.meta.env.VITE_APP_BASE_URL;

const KycVerification = () => {
  const { user } = useUserState();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [kycData, setKycData] = useState(null);
  const [accountType, setAccountType] = useState("personal");

  useEffect(() => {
    fetchKycStatus();
  }, []);

  const fetchKycStatus = async (isRefresh = false) => {
    try {
      if (!isRefresh) setLoading(true);
      const response = await axios.get(`${BASE_URL}api/kyc/my-kyc`, {
        headers: {
          Authorization: user?.token,
        },
      });

      // The backend returns { data: { kyc: { ... } } }
      if (response.data?.statusCode === 200 && response.data?.data?.kyc) {
        const kyc = response.data.data.kyc;

        // Flatten documents for easier access in the UI
        const flattenedKyc = {
          ...kyc,
          address_proof: kyc.documents?.find(d => d.document_type === "address_proof")?.file_url,
          signed_w9: kyc.documents?.find(d => d.document_type === "signed_w9")?.file_url,
          other_document: kyc.documents?.find(d => d.document_type === "other_document")?.file_url,
        };

        setKycData(flattenedKyc);
        setAccountType(kyc.account_type || "personal");
      }
    } catch (error) {
      console.error("Error fetching KYC status:", error);
      if (error.response?.status !== 404) {
        message.error("Failed to load KYC status.");
      }
    } finally {
      if (!isRefresh) setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append("account_type", values.account_type);
      formData.append("ein_ssn", values.ein_ssn);

      if (values.address_proof) {
        formData.append("address_proof", values.address_proof);
      }
      if (values.signed_w9) {
        formData.append("signed_w9", values.signed_w9);
      }
      if (values.other_document) {
        formData.append("other_document", values.other_document);
      }
      formData.append("role", user?.role[0]);
      const response = await axios.post(`${BASE_URL}api/kyc/submit`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: user?.token,
        },
      });
      console.log(response.data, "response.data", response.data?.statusCode);
      if (response.data?.statusCode == 200) {
        message.success("KYC submitted successfully!");
        fetchKycStatus(true); // Refresh data silently
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (error) {
      console.error("Error submitting KYC:", error);
      message.error(error.response?.data?.message || "Failed to submit KYC. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return { color: "success", text: "Approved", icon: <CheckCircleFilled />, tagColor: "#52c41a" };
      case "rejected":
        return { color: "error", text: "Rejected", icon: <CloseCircleFilled />, tagColor: "#ff4d4f" };
      case "pending":
      default:
        return { color: "warning", text: "Pending Review", icon: <ClockCircleFilled />, tagColor: "#faad14" };
    }
  };

  const renderFileLink = (url, label) => {
    if (!url) return null;
    const fullUrl = getSafeUrl(url.replace(/\\/g, "/"));
    const isPdf = fullUrl.toLowerCase().endsWith(".pdf");
    return (
      <div style={{ marginTop: 8 }}>
        <Space>
          {isPdf ? <FilePdfOutlined style={{ color: "#ff4d4f" }} /> : <PictureOutlined style={{ color: "#1890ff" }} />}
          <a href={fullUrl} target="_blank" rel="noopener noreferrer">
            View {label}
          </a>
        </Space>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" tip="Loading KYC Details..." />
      </div>
    );
  }

  const isApproved = kycData?.status === "approved";
  const isPending = kycData?.status === "pending";
  const statusConfig = getStatusConfig(kycData?.status);

  return (
    <div className="main-box">
      <div className="content-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <div>
            <h3 style={{ margin: 0, color: "#333", fontWeight: 700 }}>KYC Verification</h3>
            <p style={{ margin: 0, color: "#666", fontSize: "14px", fontWeight: 400 }}>
              Upload your verification documents for account approval.
            </p>
          </div>
          {kycData && (
            <Tag
              color={statusConfig.tagColor}
              icon={statusConfig.icon}
              style={{
                padding: "6px 16px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: 600,
                border: "none",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              {statusConfig.text.toUpperCase()}
            </Tag>
          )}
        </div>
      </div>

      <div className="content-middle" style={{ padding: "40px 24px", display: "block" }}>
        {isApproved && (
          <Alert
            message={<span style={{ fontWeight: 600 }}>KYC Verified</span>}
            description="Your account has been fully verified."
            type="success"
            showIcon
            icon={<CheckCircleFilled />}
            style={{ marginBottom: "32px", borderRadius: "12px", padding: "16px" }}
          />
        )}

        {isPending && (
          <Alert
            message={<span style={{ fontWeight: 600 }}>Under Review</span>}
            description="Your documents are currently being reviewed by our team."
            type="warning"
            showIcon
            icon={<ClockCircleFilled />}
            style={{ marginBottom: "32px", borderRadius: "12px", padding: "16px" }}
          />
        )}

        {kycData?.status === "rejected" && (
          <Alert
            message={<span style={{ fontWeight: 600 }}>KYC Rejected</span>}
            description={`Reason: ${kycData?.rejection_reason || "Documents did not meet our requirements. Please re-submit."}`}
            type="error"
            showIcon
            icon={<CloseCircleFilled />}
            style={{ marginBottom: "32px", borderRadius: "12px", padding: "16px" }}
          />
        )}

        {(!kycData || kycData.status === "rejected") ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
              account_type: accountType,
              ein_ssn: kycData?.ein_ssn || ""
            }}
            requiredMark={(label, { required }) => (
              <span style={{ fontWeight: 600, color: "#333" }}>
                {label} {required && <span style={{ color: "#ff4d4f" }}>*</span>}
              </span>
            )}
          >
            <Row gutter={32}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="account_type"
                  label="Account Type"
                  rules={[{ required: true, message: "Please select account type" }]}
                >
                  <Select
                    size="large"
                    placeholder="Select Type"
                    onChange={(val) => setAccountType(val)}
                    style={{ borderRadius: "8px" }}
                  >
                    <Option value="personal">Personal</Option>
                    <Option value="business">Business</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="ein_ssn"
                  label={accountType === "business" ? "EIN Number" : "SSN Number"}
                  rules={[{ required: true, message: `Please enter your ${accountType === "business" ? "EIN" : "SSN"}` }]}
                >
                  <Input
                    size="large"
                    placeholder={accountType === "business" ? "Enter EIN" : "Enter SSN"}
                    style={{ borderRadius: "8px" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div style={{ margin: "40px 0 24px" }}>
              <Divider orientation="left" style={{ margin: 0 }}>
                <span style={{ fontWeight: 700, fontSize: "16px", color: "#333" }}>Documents</span>
              </Divider>
            </div>

            <Row gutter={[32, 32]}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="address_proof"
                  rules={[{ required: true, message: "Address proof is required" }]}
                >
                  <KycUploadField
                    label="Address Proof"
                    onChange={(file) => form.setFieldsValue({ address_proof: file })}
                    value={form.getFieldValue("address_proof")}
                    required
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="signed_w9"
                  rules={[{
                    required: accountType === "business",
                    message: "Signed W9 form is required for business accounts"
                  }]}
                >
                  <KycUploadField
                    label="Signed W9 Form"
                    onChange={(file) => form.setFieldsValue({ signed_w9: file })}
                    value={form.getFieldValue("signed_w9")}
                    required={accountType === "business"}
                    extra={
                      <Button
                        type="link"
                        size="small"
                        icon={<FilePdfOutlined />}
                        href={w9FormAsset}
                        download="fw9.pdf"
                        style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                      >
                        Download W9 Form
                      </Button>
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="other_document"
                >
                  <KycUploadField
                    label="Other Document"
                    onChange={(file) => form.setFieldsValue({ other_document: file })}
                    value={form.getFieldValue("other_document")}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div style={{ marginTop: "48px", textAlign: "center" }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={submitting}
                disabled={isPending}
                icon={<CloudUploadOutlined />}
                style={{
                  height: "54px",
                  borderRadius: "45px",
                  fontWeight: "700",
                  padding: "0 60px",
                  backgroundColor: "#0474ba",
                  border: "none",
                  fontSize: "16px"
                }}
              >
                Submit KYC
              </Button>
            </div>
          </Form>
        ) : (
          <div className="profile-content" style={{ padding: "0" }}>
            <div style={{ display: "flex", gap: "64px", marginBottom: "40px" }}>
              <div style={{ flex: 1 }}>
                <Paragraph style={{ marginBottom: 0 }}>
                  <Text type="secondary" style={{ fontSize: "14px", fontWeight: 600 }}>Account Type</Text>
                  <div style={{ textTransform: "capitalize", fontSize: "18px", fontWeight: 700, color: "#333", marginTop: "4px" }}>
                    {kycData?.account_type}
                  </div>
                </Paragraph>
              </div>
              <div style={{ flex: 1 }}>
                <Paragraph style={{ marginBottom: 0 }}>
                  <Text type="secondary" style={{ fontSize: "14px", fontWeight: 600 }}>
                    {kycData?.account_type === "business" ? "EIN Number" : "SSN Number"}
                  </Text>
                  <div style={{ fontSize: "18px", fontWeight: 700, color: "#333", marginTop: "4px" }}>
                    {kycData?.ein_ssn}
                  </div>
                </Paragraph>
              </div>
            </div>

            <div style={{ margin: "40px 0 24px" }}>
              <Divider orientation="left" style={{ margin: 0 }}>
                <span style={{ fontWeight: 700, fontSize: "16px", color: "#333" }}>Uploaded Documents</span>
              </Divider>
            </div>

            <Row gutter={[24, 24]}>
              <Col xs={24} sm={8}>
                <Card
                  size="small"
                  title={<span style={{ fontWeight: 600, fontSize: "14px" }}>Address Proof</span>}
                  style={{ borderRadius: "12px", border: "1px solid #eee", background: "#fafafa" }}
                >
                  {renderFileLink(kycData?.address_proof, "Address Proof")}
                </Card>
              </Col>
              {kycData?.signed_w9 && (
                <Col xs={24} sm={8}>
                  <Card
                    size="small"
                    title={<span style={{ fontWeight: 600, fontSize: "14px" }}>W9 Form</span>}
                    style={{ borderRadius: "12px", border: "1px solid #eee", background: "#fafafa" }}
                  >
                    {renderFileLink(kycData?.signed_w9, "W9 Form")}
                  </Card>
                </Col>
              )}
              {kycData?.other_document && (
                <Col xs={24} sm={8}>
                  <Card
                    size="small"
                    title={<span style={{ fontWeight: 600, fontSize: "14px" }}>Other Document</span>}
                    style={{ borderRadius: "12px", border: "1px solid #eee", background: "#fafafa" }}
                  >
                    {renderFileLink(kycData?.other_document, "Other Document")}
                  </Card>
                </Col>
              )}
            </Row>

            {!isApproved && !isPending && (
              <div style={{ marginTop: "40px", textAlign: "center" }}>
                <button
                  className="druyp-btn"
                  onClick={() => setKycData(null)}
                  style={{ padding: "12px 40px" }}
                >
                  Edit Details
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default KycVerification;
